<template>
  <div class="review-quality-filters">
    <div class="filter-group">
      <label class="filter-label">
        {{ $t('Minimum Guest Score') }}: {{ minRating.toFixed(1) }}
      </label>
      <div class="slider-container">
        <input
          type="range"
          :min="0"
          :max="10"
          :step="0.1"
          v-model="minRating"
          @input="updateMinRating"
          class="rating-slider"
        />
        <div class="slider-labels">
          <span>0</span>
          <span>5</span>
          <span>10</span>
        </div>
      </div>
    </div>

    <div class="filter-group">
      <label class="filter-label">
        {{ $t('Minimum Reviews') }}: {{ minReviews }}{{ minReviews >= maxReviewsLimit ? '+' : '' }}
      </label>
      <div class="slider-container">
        <input
          type="range"
          :min="0"
          :max="maxReviewsLimit"
          :step="reviewsStep"
          v-model="minReviews"
          @input="updateMinReviews"
          class="reviews-slider"
        />
        <div class="slider-labels">
          <span>0</span>
          <span>{{ Math.floor(maxReviewsLimit / 2) }}</span>
          <span>{{ maxReviewsLimit }}+</span>
        </div>
      </div>
    </div>

    <div class="quality-indicators">
      <div class="quality-chip" :class="getQualityClass(minRating, minReviews)">
        {{ getQualityLabel(minRating, minReviews) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSearchStore } from '~/store/search'

const emit = defineEmits(['update'])

const route = useRoute()
const searchStore = useSearchStore()

const minRating = ref(0)
const minReviews = ref(0)
const maxReviewsLimit = 500
const reviewsStep = 10

// Calculate dynamic max reviews based on available data
const dynamicMaxReviews = computed(() => {
  const hotels = searchStore.hotelsProducts || []
  let maxReviews = 100
  
  hotels.forEach((hotel: any) => {
    if (hotel.reviews_count && hotel.reviews_count > maxReviews) {
      maxReviews = Math.min(hotel.reviews_count, maxReviewsLimit)
    }
  })
  
  return Math.ceil(maxReviews / reviewsStep) * reviewsStep
})

// Initialize from route query
onMounted(() => {
  const ratingQuery = route.query.min_rating as string
  const reviewsQuery = route.query.min_reviews as string
  
  if (ratingQuery) {
    minRating.value = parseFloat(ratingQuery) || 0
  }
  
  if (reviewsQuery) {
    minReviews.value = parseInt(reviewsQuery) || 0
  }
})

// Watch route changes
watch([
  () => route.query.min_rating,
  () => route.query.min_reviews
], ([newRating, newReviews]) => {
  minRating.value = newRating ? parseFloat(newRating as string) || 0 : 0
  minReviews.value = newReviews ? parseInt(newReviews as string) || 0 : 0
})

const updateMinRating = () => {
  emit('update', {
    minRating: minRating.value,
    minReviews: minReviews.value
  })
}

const updateMinReviews = () => {
  emit('update', {
    minRating: minRating.value,
    minReviews: minReviews.value
  })
}

const getQualityClass = (rating: number, reviews: number) => {
  if (rating >= 8.5 && reviews >= 100) return 'excellent'
  if (rating >= 7.5 && reviews >= 50) return 'very-good'
  if (rating >= 6.5 && reviews >= 20) return 'good'
  if (rating >= 5.0 || reviews >= 10) return 'fair'
  return 'any'
}

const getQualityLabel = (rating: number, reviews: number) => {
  const qualityClass = getQualityClass(rating, reviews)
  
  switch (qualityClass) {
    case 'excellent': return 'Excellent Quality'
    case 'very-good': return 'Very Good Quality'
    case 'good': return 'Good Quality'
    case 'fair': return 'Fair Quality'
    default: return 'Any Quality'
  }
}
</script>

<style scoped>
.review-quality-filters {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.slider-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rating-slider,
.reviews-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e0e0e0;
  outline: none;
  appearance: none;
}

.rating-slider::-webkit-slider-thumb,
.reviews-slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #1985F9;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.rating-slider::-moz-range-thumb,
.reviews-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #1985F9;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
}

.quality-indicators {
  display: flex;
  justify-content: center;
}

.quality-chip {
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
}

.quality-chip.excellent {
  background-color: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #4caf50;
}

.quality-chip.very-good {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #2196f3;
}

.quality-chip.good {
  background-color: #fff3e0;
  color: #ef6c00;
  border: 1px solid #ff9800;
}

.quality-chip.fair {
  background-color: #fce4ec;
  color: #c2185b;
  border: 1px solid #e91e63;
}

.quality-chip.any {
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #e0e0e0;
}
</style>